import { Checkbox, Select, Text, Textarea, Tooltip } from '@mantine/core';
import { Task } from '@medplum/fhirtypes';
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table';
import { ReactElement, useMemo } from 'react';
import dayjs from 'dayjs';
import { PreprocedureTaskCode } from './constants';
import { IssuesCell } from './IssuesCell';
import { LookupSource } from './types';
import { PatientCell, ProcedureCell } from './PatientCell';
import { CLEARANCE_LABEL_TO_STATUS, ClearanceLabel, ClinicalRow, toEt } from './rows';

export interface ClinicalTableProps {
  rows: ClinicalRow[];
  unavailable: LookupSource[];
  /** True while a save for this task is in flight, or when tasks could not be read. */
  isTaskLocked: (patientId: string, code: PreprocedureTaskCode) => boolean;
  onTaskChange: (row: ClinicalRow, code: PreprocedureTaskCode, status: Task['status']) => Promise<void>;
  onClearanceNoteChange: (row: ClinicalRow, note: string) => Promise<void>;
}

function Unknown(): ReactElement {
  return (
    <Text size="sm" c="dimmed">
      —
    </Text>
  );
}

const CLEARANCE_OPTIONS = Object.keys(CLEARANCE_LABEL_TO_STATUS) as ClearanceLabel[];

export function ClinicalTable({
  rows,
  unavailable,
  isTaskLocked,
  onTaskChange,
  onClearanceNoteChange,
}: ClinicalTableProps): ReactElement {
  const unknown = useMemo(() => new Set(unavailable), [unavailable]);
  const columns = useMemo<MRT_ColumnDef<ClinicalRow>[]>(
    () => [
      {
        id: 'issues',
        header: 'Status',
        size: 90,
        accessorFn: (r) => r.issues.length,
        Cell: ({ row }) => <IssuesCell issues={row.original.issues} />,
      },
      {
        id: 'patient',
        header: 'Patient',
        accessorFn: (r) => r.patientName ?? '',
        Cell: ({ row }) => (
          <PatientCell
            patientId={row.original.patientId}
            patientName={row.original.patientName}
            patientDOB={row.original.patientDOB}
          />
        ),
      },
      {
        id: 'procedure',
        header: 'Procedure',
        accessorFn: (r) => r.procedureDate ?? '',
        Cell: ({ row }) => (
          <ProcedureCell
            typeCode={row.original.typeCode}
            typeDisplay={row.original.typeDisplay}
            dateLabel={toEt(row.original.procedureDate)?.format('MMM D') ?? null}
            locationName={row.original.locationName}
          />
        ),
      },
      { id: 'provider', header: 'Provider', accessorFn: (r) => r.providerName ?? '' },
      { id: 'navigator', header: 'Navigator', accessorFn: (r) => r.navigatorName ?? '' },
      {
        id: 'rx',
        header: 'Rx',
        accessorFn: (r) => r.rx?.count ?? 0,
        Cell: ({ row }) => {
          const rx = row.original.rx;
          if (unknown.has('rx')) {
            return <Unknown />;
          }
          if (!rx?.count) {
            return (
              <Text size="sm" c="red">
                Missing
              </Text>
            );
          }
          const last = rx.lastWrittenAt ? ` · last ${dayjs(rx.lastWrittenAt).format('M/D/YYYY')}` : '';
          return (
            <Tooltip label={`Pharmacy verified ${rx.eRxSentCount}${last}`}>
              <Text size="sm">{rx.count} orders</Text>
            </Tooltip>
          );
        },
      },
      {
        id: 'labs',
        header: 'Labs',
        accessorFn: (r) => r.labs?.aggregatedStatus ?? '',
        Cell: ({ row }) => {
          const labs = row.original.labs;
          if (unknown.has('labs')) {
            return <Unknown />;
          }
          if (!labs) {
            return (
              <Text size="sm" c="dimmed">
                None
              </Text>
            );
          }
          const lines = [
            `${labs.count} orders, ${labs.completedCount} completed`,
            labs.firstOrdererName
              ? `First by ${labs.firstOrdererName}${labs.firstCreatedDate ? ` on ${dayjs(labs.firstCreatedDate).format('M/D/YYYY')}` : ''}`
              : null,
            labs.labs.length ? labs.labs.join(', ') : null,
          ].filter(Boolean);
          return (
            <Tooltip multiline w={280} label={lines.join('\n')} style={{ whiteSpace: 'pre-line' }}>
              <Text size="sm">{labs.aggregatedStatus}</Text>
            </Tooltip>
          );
        },
      },
      {
        id: 'labsReviewed',
        header: 'Labs reviewed',
        accessorFn: (r) => r.labsReviewed?.date ?? '',
        Cell: ({ row }) => {
          const reviewed = row.original.labsReviewed;
          if (unknown.has('labsReviewed')) {
            return <Unknown />;
          }
          if (!reviewed?.date) {
            return (
              <Text size="sm" c="dimmed">
                No
              </Text>
            );
          }
          return (
            <Text size="sm">
              {dayjs(reviewed.date).format('M/D/YYYY')}
              {reviewed.author ? ` · ${reviewed.author}` : ''}
            </Text>
          );
        },
      },
      {
        id: 'clearance',
        header: 'Clearance',
        size: 150,
        accessorFn: (r) => r.clearanceStatus ?? '',
        Cell: ({ row }) => (
          <Select
            size="xs"
            data={CLEARANCE_OPTIONS}
            value={row.original.clearanceStatus}
            placeholder="—"
            allowDeselect={false}
            disabled={isTaskLocked(row.original.patientId, 'clearance')}
            onChange={(value) => {
              if (value) {
                void onTaskChange(row.original, 'clearance', CLEARANCE_LABEL_TO_STATUS[value as ClearanceLabel]);
              }
            }}
          />
        ),
      },
      {
        id: 'clearanceNote',
        header: 'Clearance notes',
        size: 240,
        accessorFn: (r) => r.clearanceNote ?? '',
        Cell: ({ row }) => (
          <Textarea
            key={`${row.original.id}-${row.original.clearanceNote ?? ''}`}
            size="xs"
            autosize
            minRows={1}
            defaultValue={row.original.clearanceNote ?? ''}
            disabled={isTaskLocked(row.original.patientId, 'clearance')}
            onBlur={(e) => {
              const value = e.currentTarget.value;
              if (value.trim() !== (row.original.clearanceNote ?? '')) {
                void onClearanceNoteChange(row.original, value);
              }
            }}
          />
        ),
      },
      {
        id: 'infoCollected',
        header: 'Info collected',
        size: 90,
        accessorFn: (r) => String(r.infoCollected),
        Cell: ({ row }) => (
          <Checkbox
            checked={row.original.infoCollected === true}
            disabled={isTaskLocked(row.original.patientId, 'infoCollected')}
            onChange={(e) =>
              void onTaskChange(row.original, 'infoCollected', e.currentTarget.checked ? 'completed' : 'requested')
            }
          />
        ),
      },
      {
        id: 'needsMonitoring',
        header: 'Needs monitoring',
        size: 90,
        accessorFn: (r) => String(r.needsMonitoring),
        Cell: ({ row }) => (
          <Checkbox
            checked={row.original.needsMonitoring === true}
            disabled={isTaskLocked(row.original.patientId, 'needsMonitoring')}
            onChange={(e) =>
              void onTaskChange(row.original, 'needsMonitoring', e.currentTarget.checked ? 'completed' : 'requested')
            }
          />
        ),
      },
    ],
    [onTaskChange, onClearanceNoteChange, unknown, isTaskLocked]
  );

  const table = useMantineReactTable({
    columns,
    data: rows,
    getRowId: (r) => r.id,
    enablePagination: false,
    enableColumnActions: false,
    enableGlobalFilter: false,
    enableTopToolbar: false,
    enableStickyHeader: true,
    mantineTableContainerProps: { style: { maxHeight: 'calc(100vh - 280px)' } },
  });

  return <MantineReactTable table={table} />;
}
